import { useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { getContactDetails, checkContactStatus } from 'services/rocketReach'
import { getErrorMessage } from 'constants/rocketReachErrors'
import { useNotification } from 'components/Notification'

export const lookupQueryKeys = {
  contactLookup: id => ['rocketReach', 'contactLookup', id],
  statusCheck: id => ['rocketReach', 'statusCheck', id],
}

/**
 * RocketReach lookup hook - handles contact lookup fetching and polling
 * @returns {object} - contact lookup functions and utilities
 */
const useRocketReachLookup = () => {
  const { createNotification } = useNotification()
  const queryClient = useQueryClient()
  const pollingIntervalsRef = useRef(new Map())
  const [pollingContacts, setPollingContacts] = useState(new Set())

  // Cleanup polling on unmount
  useEffect(() => {
    const currentIntervals = pollingIntervalsRef.current
    return () => {
      currentIntervals.forEach(intervalId => clearInterval(intervalId))
      currentIntervals.clear()
    }
  }, [])

  // Handle getting contact lookup using fetchQuery
  const handleGetContactDetails = async targetContactId => {
    // Invalidate existing cache to force fresh fetch
    await queryClient.invalidateQueries({
      queryKey: lookupQueryKeys.contactLookup(targetContactId),
    })

    const response = await queryClient.fetchQuery({
      queryKey: lookupQueryKeys.contactLookup(targetContactId),
      queryFn: () => getContactDetails(targetContactId),
      staleTime: 1000 * 60 * 2, // 2 minutes
      gcTime: 1000 * 60 * 10, // 10 minutes
    })

    if (response?.error) {
      stopStatusPolling(targetContactId)
      // Clear cache for this contact when error occurs
      queryClient.removeQueries({ queryKey: lookupQueryKeys.contactLookup(targetContactId) })
      const errorMessage = getErrorMessage(response.error)
      createNotification({
        message: `Error fetching contact lookup: ${errorMessage}`,
        variant: 'error',
      })
      return
    }

    if (response?.data?.requiresStatusCheck) {
      startStatusPolling(targetContactId)
    }
  }

  // Check contact status using fetchQuery
  const checkStatus = async targetContactId => {
    const response = await queryClient.fetchQuery({
      queryKey: lookupQueryKeys.statusCheck(targetContactId),
      queryFn: () => checkContactStatus(targetContactId),
    })

    if (response?.error) {
      stopStatusPolling(targetContactId)
      // Clear cache for both status check and contact lookup when error occurs
      queryClient.removeQueries({ queryKey: lookupQueryKeys.statusCheck(targetContactId) })
      const errorMessage = getErrorMessage(response.error)
      createNotification({
        message: `Error checking contact status: ${errorMessage}`,
        variant: 'error',
      })
      return
    }

    if (response?.data) {
      // Update the cached contact lookup with new status
      const existingLookup = queryClient.getQueryData(
        lookupQueryKeys.contactLookup(targetContactId),
      )
      if (existingLookup && existingLookup.data) {
        queryClient.setQueryData(lookupQueryKeys.contactLookup(targetContactId), {
          data: {
            ...existingLookup.data,
            ...response.data,
          },
        })
      }

      // Stop polling if status is complete
      if (!response.data?.requiresStatusCheck) {
        stopStatusPolling(targetContactId)
      }
    }
  }

  // Start status polling for a contact
  const startStatusPolling = targetContactId => {
    // Don't start if already polling
    if (pollingIntervalsRef.current.has(targetContactId)) {
      return
    }

    // Add to polling contacts set
    setPollingContacts(prev => new Set([...prev, targetContactId]))

    const intervalId = setInterval(() => {
      checkStatus(targetContactId)
    }, 5000) // Poll every 5 seconds

    pollingIntervalsRef.current.set(targetContactId, intervalId)
  }

  // Stop status polling for a contact
  const stopStatusPolling = targetContactId => {
    const intervalId = pollingIntervalsRef.current.get(targetContactId)
    if (intervalId) {
      clearInterval(intervalId)
      pollingIntervalsRef.current.delete(targetContactId)
    }

    // Remove from polling contacts set
    setPollingContacts(prev => {
      const newSet = new Set(prev)
      newSet.delete(targetContactId)
      return newSet
    })
  }

  // Helper to check if contact is in polling state
  const isContactPolling = targetContactId => {
    return pollingContacts.has(targetContactId)
  }

  return { handleGetContactDetails, isContactPolling }
}

export default useRocketReachLookup
